import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Sparkles, Wand2, Loader2, CheckCircle2, XCircle, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { UnauthorizedState } from '@/components/states';
import { usePlayer } from '@/context/PlayerContext';
import { Paywall } from '@/components/Paywall';
import { STYLES } from '@/data/styles';

export default function Create() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { session, profile, refreshProfile } = useAuth();
  const { playTrack } = usePlayer();
  const [styles, setStyles] = useState([]);
  const [styleId, setStyleId] = useState(null);
  const [prompt, setPrompt] = useState(params.get('prompt') || '');
  const [lyrics, setLyrics] = useState(params.get('lyrics') || '');
  const [styleText, setStyleText] = useState(params.get('style_text') || '');
  const [title, setTitle] = useState(params.get('title') || '');
  const [status, setStatus] = useState(null); // queued|generating|completed|failed
  const [resultSong, setResultSong] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const pollRef = useRef(null);
  // Studio: when creating a new version, /create is opened with a project_id so
  // the resulting generation is attached to that project.
  const projectId = params.get('project_id') || null;
  const defaultTab = (params.get('lyrics') || params.get('style_text') || params.get('title')) ? 'advanced' : 'simple';
  // Tracks whether the current style was auto-selected from the ?style= URL param
  // (vs an explicit pill click). Lets a free-text description reclaim priority.
  const preselectedStyle = useRef(false);

  useEffect(() => {
    const applyStyles = (list) => {
      setStyles(list);
      const slug = params.get('style');
      if (slug) {
        const s = list.find((x) => x.slug === slug);
        if (s) {
          setStyleId(s.id); setStyleText(s.name);
          preselectedStyle.current = true;
          if (!prompt) setPrompt(s.default_prompt || '');
        }
      }
    };
    api.get('/styles')
      .then(({ data }) => applyStyles(data && data.length ? data : STYLES))
      .catch(() => applyStyles(STYLES));
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) return <UnauthorizedState />;

  const poll = (jobId) => {
    let errors = 0;
    let ticks = 0;
    const MAX_TICKS = 80; // ~200s safety cap
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_TICKS) {
        clearInterval(pollRef.current);
        setStatus('failed');
        toast.error('La génération prend trop de temps. Réessaie.');
        return;
      }
      try {
        const { data } = await api.get(`/generation/${jobId}`);
        errors = 0;
        setStatus(data.status);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRef.current);
          if (data.status === 'completed') {
            setResultSong(data.song);
            toast.success('Ta musique est prête ! Elle est dans ta Bibliothèque.');
            refreshProfile?.();
          } else {
            toast.error(data.error_message || 'La génération a échoué.');
            refreshProfile?.(); // a failed generation refunds the reserved credit
          }
        }
      } catch {
        // tolerate transient errors (e.g. backend restart window) before failing
        errors += 1;
        if (errors >= 4) {
          clearInterval(pollRef.current);
          setStatus('failed');
          toast.error('Connexion au service interrompue pendant la génération.');
        }
      }
    }, 2500);
  };

  const generate = async () => {
    if (!prompt.trim() && !lyrics.trim()) { toast.error('Ajoute une description ou des paroles.'); return; }
    if ((profile?.credits_balance ?? 0) <= 0) { setPaywall(true); return; }
    setStatus('queued');
    setResultSong(null);
    try {
      const { data } = await postGenerate({
        prompt: prompt.trim() || null,
        lyrics: lyrics.trim() || null,
        style_id: styleId && !String(styleId).startsWith('local-') ? styleId : null,
        style_text: styleText.trim() || null,
        title: title.trim() || null,
        project_id: projectId,
      });
      poll(data.job_id);
    } catch (e) {
      setStatus(null);
      if (e?.response?.status === 402) { setPaywall(true); return; }
      setStatus('failed');
      toast.error(apiError(e));
    }
  };

  const busy = status === 'queued' || status === 'generating';

  // In simple mode, a free-text description written by the user takes priority
  // over a style that was merely preselected from the ?style= URL param.
  const onSimplePromptChange = (value) => {
    setPrompt(value);
    if (preselectedStyle.current) {
      preselectedStyle.current = false;
      setStyleId(null);
      setStyleText('');
    }
  };

  // POST the generation with a bounded retry, ONLY for transient transport/proxy
  // failures (network drop, or a bare gateway 404/502/503/504 emitted by the
  // ingress while the backend is momentarily restarting). Functional errors
  // (401/403/422 — always carry a FastAPI "detail") are never retried, so we
  // never trigger a second paid generation.
  const isTransient = (e) => {
    if (!e?.response) return true; // network / connection reset — request never landed
    return [404, 502, 503, 504].includes(e.response.status) && !e.response.data?.detail;
  };
  const postGenerate = async (payload, attempt = 0) => {
    try {
      return await api.post('/songs/generate', payload);
    } catch (e) {
      if (isTransient(e) && attempt < 2) {
        await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
        return postGenerate(payload, attempt + 1);
      }
      throw e;
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">Créer</h1>
        <p className="mt-1 text-muted-foreground">Le moteur de création de MyMusic IA.</p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-3.5 py-1.5 text-sm" data-testid="create-credits">
          <Sparkles className="h-4 w-4 text-brand" />
          <span className="text-white">Crédits : <span className="font-bold text-brand">{profile?.credits_balance ?? 0}</span></span>
          <button onClick={() => navigate('/credits')} className="ml-1 rounded-full bg-brand/20 px-2 py-0.5 text-xs font-semibold text-brand hover:bg-brand/30" data-testid="create-buy-credits">Acheter</button>
        </div>
        {projectId && (
          <div className="mt-3 rounded-xl border border-brand/40 bg-brand/10 px-4 py-2.5 text-sm text-brand" data-testid="create-version-banner">
            Nouvelle version d'un projet Studio — ajuste les paramètres puis lance la génération. Le morceau sera ajouté au projet.
          </div>
        )}
      </div>

      <StyleSelector styles={styles} styleId={styleId} onSelect={(s) => { preselectedStyle.current = false; setStyleId(s.id); setStyleText(s.name); }} />

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-surface-card">
          <TabsTrigger value="simple" data-testid="mode-simple">Mode simple</TabsTrigger>
          <TabsTrigger value="advanced" data-testid="mode-advanced">Mode avancé</TabsTrigger>
        </TabsList>

        <TabsContent value="simple" className="mt-5 space-y-4">
          <Field label="Description de la chanson">
            <textarea value={prompt} onChange={(e) => onSimplePromptChange(e.target.value)} rows={4}
                      placeholder="Décrivez la chanson que vous souhaitez créer..."
                      data-testid="create-prompt-input"
                      className="w-full resize-none rounded-xl border border-border bg-surface-input px-4 py-3 text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none" />
          </Field>
        </TabsContent>

        <TabsContent value="advanced" className="mt-5 space-y-4">
          <Field label="Titre (optionnel)">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre du morceau"
                   data-testid="create-title-input"
                   className="w-full rounded-xl border border-border bg-surface-input px-4 py-3 text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none" />
          </Field>
          <Field label="Paroles">
            <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={8}
                      placeholder={"[Couplet 1]\nÉcris ou colle tes paroles ici...\n\n[Refrain]\n..."}
                      data-testid="create-lyrics-input"
                      className="w-full resize-none rounded-xl border border-border bg-surface-input px-4 py-3 font-mono text-sm text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none" />
          </Field>
          <Field label="Style / ambiance / instrumentation">
            <input value={styleText} onChange={(e) => setStyleText(e.target.value)}
                   placeholder="ex: Rap Ivoire, énergique, 808 lourdes, ambiance nocturne"
                   data-testid="create-style-input"
                   className="w-full rounded-xl border border-border bg-surface-input px-4 py-3 text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none" />
          </Field>
          <Field label="Description / prompt">
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                      placeholder="Décris l'intention générale du morceau..."
                      className="w-full resize-none rounded-xl border border-border bg-surface-input px-4 py-3 text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none" />
          </Field>
        </TabsContent>
      </Tabs>

      <button onClick={generate} disabled={busy} data-testid="generate-song-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 font-display text-lg font-bold text-black btn-glow transition-transform hover:scale-[1.01] disabled:opacity-60">
        {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Génération...</> : <><Sparkles className="h-5 w-5" /> Créer</>}
      </button>

      {status && <GenerationStatus status={status} song={resultSong}
                                   onPlay={() => resultSong && playTrack({
                                     song_id: resultSong.id, title: resultSong.title, slug: resultSong.slug,
                                     cover_url: resultSong.cover_url, audio_url: resultSong.audio_url,
                                     style_text: resultSong.style_text, duration_seconds: resultSong.duration_seconds,
                                   }, [])}
                                   onLibrary={() => navigate('/library')} />}
      <Paywall open={paywall} onOpenChange={setPaywall} feature="La génération musicale" />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-2">
      <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function StyleSelector({ styles, styleId, onSelect }) {
  return (
    <div>
      <p className="mb-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">Style musical</p>
      <div className="flex flex-wrap gap-2">
        {styles.map((s) => (
          <button key={s.id} onClick={() => onSelect(s)} data-testid={`style-pill-${s.slug}`}
                  className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
                    styleId === s.id ? 'border-brand bg-brand/15 text-brand' : 'border-border bg-surface-card text-muted-foreground hover:text-white'
                  }`}>
            {s.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function GenerationStatus({ status, song, onPlay, onLibrary }) {
  if (status === 'completed' && song) {
    return (
      <div className="card-surface animate-fade-up flex items-center gap-4 border-brand/40 p-5" data-testid="generation-result">
        <img src={song.cover_url || 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=200'} alt="" className="h-20 w-20 rounded-xl object-cover" />
        <div className="flex-1">
          <div className="flex items-center gap-2 text-[#22C55E]"><CheckCircle2 className="h-4 w-4" /><span className="font-mono text-xs uppercase">Terminé</span></div>
          <h3 className="font-display text-lg font-bold text-white">{song.title}</h3>
          <p className="text-sm text-muted-foreground">{song.style_text}</p>
        </div>
        <div className="flex flex-col gap-2">
          <button onClick={onPlay} className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-black btn-glow" data-testid="result-play-button">
            <Play className="h-4 w-4 fill-black" /> Écouter
          </button>
          <button onClick={onLibrary} className="rounded-full bg-surface-card px-4 py-2 text-sm text-white hover:bg-surface-cardHover" data-testid="result-library-button">
            Bibliothèque
          </button>
        </div>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="card-surface flex items-center gap-3 border-destructive/40 p-5" data-testid="generation-failed">
        <XCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-muted-foreground">La génération a échoué. Réessaie dans un instant.</p>
      </div>
    );
  }
  return (
    <div className="card-surface flex items-center gap-4 p-5" data-testid="generation-progress">
      <div className="flex items-end gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className="eq-bar w-1.5 rounded-full bg-brand" style={{ height: 24, animationDelay: `${i * 0.12}s` }} />
        ))}
      </div>
      <div>
        <p className="font-display font-semibold text-white">{status === 'queued' ? 'En file d\'attente...' : 'Génération en cours...'}</p>
        <p className="text-sm text-muted-foreground">MyMusic IA compose ta musique, patiente quelques instants.</p>
      </div>
    </div>
  );
}
